import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import RichTextEditor, { AttachedFile } from './RichTextEditor';
import { createPost, uploadImages } from '../../services/postApi';
import { CreatePostRequest, BoardInfo } from '../../types/post';
import { getCoffeeChatMentors, CoffeeChatMentor, getLectureChapters, LectureChapter } from '../../services/lecturerApi';
import { Chapter } from '../../services/lectureApi';
import { getMyLectureList, MyLectureListResponse } from '../../services/mypageApi';
import { searchLecturers, LecturerSearchItem } from '../../services/lecturerSearchApi';
import { getStudyCategories, StudyCategory } from '../../services/studyCategoryApi';

// 카테고리별 필수 필드 정의
const CATEGORY_FIELD_CONFIG = {
  '강의 질문': {
    required: ['title', 'htmlContent', 'category', 'lectureId', 'chapter'],
    optional: [],
    fields: {
      lectureId: { 
        type: 'select', 
        placeholder: '강의를 선택해주세요',
        label: '강의'
      },
      chapter: { 
        type: 'select', 
        placeholder: '챕터를 선택해주세요',
        label: '챕터'
      },
    }
  },
  '스터디': {
    required: ['title', 'htmlContent', 'category', 'studyCategoryId'],
    optional: [],
    fields: {
      studyCategoryId: { 
        type: 'select', 
        placeholder: '스터디 카테고리를 선택해주세요',
        label: '스터디 카테고리'
      }
    }
  },
  '커피챗': {
    required: ['title', 'htmlContent', 'category', 'lecturerId'],
    optional: [],
    fields: {
      lecturerId: { 
        type: 'select', 
        placeholder: '연자를 선택해주세요',
        label: '연자'
      }
    }
  },
  '정보 공유': {
    required: ['title', 'htmlContent', 'category'],
    optional: [],
    fields: {}
  },
  '공지사항': {
    required: ['title', 'htmlContent', 'category'],
    optional: [],
    fields: {}
  },
  '문의하기': {
    required: ['title', 'htmlContent', 'category'],
    optional: [],
    fields: {}
  },
} as const;

type CategoryName = keyof typeof CATEGORY_FIELD_CONFIG;
type DynamicFormData = {
  lectureId?: number;
  chapter?: number;
  lecturerId?: number;
  studyCategoryId?: number;
};
type DynamicFieldName = keyof DynamicFormData;

interface WritePostFormProps {
  categories?: Category[];
  boards?: BoardInfo[];
  initialCategory?: string;
  onSubmit: (postData: PostData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  lecturerId?: number; // 커피챗용 연자 ID 추가
}

interface Category {
  id: string;
  name: string;
  value: string;
}

interface PostData {
  category: string;
  board: string;
  title: string;
  content: string;
  dynamicFields?: DynamicFormData;
  attachedFiles?: AttachedFile[];
}

const WritePostForm: React.FC<WritePostFormProps> = ({
  categories = [
    { id: 'community', name: '커뮤니티', value: 'community' },
    { id: 'study', name: '스터디', value: 'study' },
    { id: 'mypage', name: '마이페이지', value: 'mypage' },
  ],
  boards = [
    { id: 'lecture-question', name: '강의 질문', category: 'community', apiCategory: '강의 질문' },
    { id: 'info-sharing', name: '정보 공유', category: 'community', apiCategory: '정보 공유' },
    { id: 'coffee-chat', name: '커피챗', category: 'community', apiCategory: '커피챗' },
    { id: 'study', name: '스터디', category: 'study', apiCategory: '스터디' },
    { id: 'inquiry', name: '문의하기', category: 'mypage', apiCategory: '문의하기' },
  ],
  initialCategory = 'community',
  onSubmit,
  onCancel,
  isSubmitting = false,
  lecturerId, // 커피챗용 연자 ID
}) => {
  const [selectedCategory] = useState(initialCategory);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);
  const [isApiSubmitting, setIsApiSubmitting] = useState(false);
  const [dynamicFields, setDynamicFields] = useState<DynamicFormData>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showDynamicDropdowns, setShowDynamicDropdowns] = useState<Record<string, boolean>>({});
  const [searchTexts, setSearchTexts] = useState<Record<string, string>>({});
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const [lectureList, setLectureList] = useState<MyLectureListResponse['data']['content']>([]);
  const [chapterList, setChapterList] = useState<LectureChapter[]>([]); // 챕터 목록 추가
  const [lecturerList, setLecturerList] = useState<LecturerSearchItem[]>([]); // 연자 검색 결과 (기존 검색용)
  const [coffeeChatMentors, setCoffeeChatMentors] = useState<CoffeeChatMentor[]>([]); // 커피챗 멘토 목록 추가
  const [lecturerSearchTimer, setLecturerSearchTimer] = useState<NodeJS.Timeout | null>(null); // 연자 검색 디바운스
  const [isLoadingLecturers, setIsLoadingLecturers] = useState(false); // 연자 로딩 상태
  const [isLoadingChapters, setIsLoadingChapters] = useState(false); // 챕터 로딩 상태 추가
  const [isLoadingCoffeeChatMentors, setIsLoadingCoffeeChatMentors] = useState(false); // 커피챗 멘토 로딩 상태 추가
  const [studyCategories, setStudyCategories] = useState<StudyCategory[]>([]); // 스터디 카테고리 목록

  // 커피챗에서 lecturerId가 전달된 경우 초기값 설정
  useEffect(() => {
    if (lecturerId && boards.some(board => board.apiCategory === '커피챗')) {
      // 커피챗 보드 자동 선택
      setSelectedBoard('coffee-chat');
      // lecturerId를 동적 필드에 설정
      setDynamicFields(prev => ({
        ...prev,
        lecturerId: lecturerId
      }));
      
      // 해당 연자 정보 조회해서 이름 설정
      const loadLecturerInfo = async () => {
        try {
          const { fetchLecturerDetail } = await import('../../services/lecturerApi');
          const response = await fetchLecturerDetail(lecturerId);
          if (response.data) {
            setSearchTexts(prev => ({
              ...prev,
              lecturerId: response.data.name
            }));
          }
        } catch (error) {
          /// console.error('초기 연자 정보 로딩 실패:', error);
        }
      };
      loadLecturerInfo();
    }
  }, [lecturerId, boards]);

  // 연자 검색 함수
  const handleLecturerSearch = async (keyword: string) => {
    try {
      setIsLoadingLecturers(true);
      // 빈 검색어면 전체 리스트 조회, 아니면 검색 결과 조회
      const results = await searchLecturers(keyword || '');
      setLecturerList(results);
    } catch (error) {
      /// console.error('연자 검색 실패:', error);
      setLecturerList([]);
    } finally {
      setIsLoadingLecturers(false);
    }
  };

  // 연자 검색 디바운스 처리
  const debouncedLecturerSearch = (query: string) => {
    if (lecturerSearchTimer) {
      clearTimeout(lecturerSearchTimer);
    }

    const timer = setTimeout(() => {
      handleLecturerSearch(query);
    }, 300);

    setLecturerSearchTimer(timer);
  };

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (lecturerSearchTimer) {
        clearTimeout(lecturerSearchTimer);
      }
    };
  }, [lecturerSearchTimer]);

  // 현재 선택된 게시판의 API 카테고리 가져오기
  const selectedBoardData = useMemo(() => 
    boards.find(board => board.id === selectedBoard), 
    [boards, selectedBoard]
  );
  
  // 현재 카테고리의 필드 설정 가져오기
  const currentFieldConfig = useMemo(() => {
    if (!selectedBoardData?.apiCategory) return null;
    return CATEGORY_FIELD_CONFIG[selectedBoardData.apiCategory as CategoryName] || null;
  }, [selectedBoardData]);

  // /// console.log('@#@#', boards, initialCategory);
  
  // 스터디 카테고리 목록 불러오기
  useEffect(() => {
    const fetchStudyCategories = async () => {
      try {
        const response = await getStudyCategories();
        if (response.code === 200 && response.data) {
          setStudyCategories(response.data);
        }
      } catch (error) {
        /// console.error('스터디 카테고리 목록 불러오기 실패:', error);
        setStudyCategories([]);
      }
    };
    
    // 컴포넌트 마운트 시 스터디 카테고리 목록 불러오기
    fetchStudyCategories();
  }, []);
  
  // 초기 연자 목록 로드 (커피챗 보드 선택 시)
  useEffect(() => {
    if (selectedBoardData?.apiCategory === '커피챗') {
      // 기존 검색 방식 대신 커피챗 멘토 목록 불러오기
      loadCoffeeChatMentors();
    }
  }, [selectedBoardData]);
  useEffect(() => {
    const fetchLectures = async () => {
      try {
        const response = await getMyLectureList();
        setLectureList(response.content);
      } catch (error) {
        /// console.error('강의 목록 불러오기 실패:', error);
        setLectureList([]);
      }
    };
    fetchLectures();
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-dropdown]')) {
        setShowBoardDropdown(false);
        setShowDynamicDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 필수 필드 검증
  const validateRequiredFields = (): boolean => {
    const errors: Record<string, string> = {};
    
    // 기본 필수 필드 검증
    if (!title.trim()) errors.title = '제목을 입력해주세요.';
    if (!content.trim()) errors.content = '내용을 입력해주세요.';
    if (!selectedBoard) errors.board = '게시판을 선택해주세요.';
    
    // 동적 필수 필드 검증
    if (currentFieldConfig) {
      currentFieldConfig.required.forEach((fieldName) => {
        if (fieldName === 'title' || fieldName === 'htmlContent' || fieldName === 'category') return;

        const dynamicName = fieldName as DynamicFieldName;
        const value = dynamicFields[dynamicName];
        if (!value || value === 0) {
          const fieldConfig = (currentFieldConfig.fields as Record<string, any>)[dynamicName];
          errors[dynamicName] = `${fieldConfig?.label || dynamicName}을(를) 선택해주세요.`;
        }
      });
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 카테고리에 따른 게시판 필터링 - 모든 게시판 표시하도록 수정
  const filteredBoards = boards; // 필터링 없이 모든 게시판 표시

  const handleBoardSelect = (boardId: string) => {
    setSelectedBoard(boardId);
    setShowBoardDropdown(false);
    // 게시판 변경 시 동적 필드와 에러 초기화
    setDynamicFields({});
    setFieldErrors({});
    setShowDynamicDropdowns({});
    setSearchTexts({});
    setAttachedFiles([]);
  };

  // 동적 필드 값 업데이트
  const updateDynamicField = (fieldName: DynamicFieldName, value: string | number) => {
    setDynamicFields(prev => ({
      ...prev,
      [fieldName]: fieldName.includes('Id') ? Number(value) : value
    }));
    
    // 에러 초기화
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // 동적 드롭다운 토글
  const toggleDynamicDropdown = (fieldName: string) => {
    setShowDynamicDropdowns(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // 검색 텍스트 업데이트
  const updateSearchText = (fieldName: string, text: string) => {
    setSearchTexts(prev => ({
      ...prev,
      [fieldName]: text
    }));
    
    // 검색 중이면 드롭다운 열기
    if (text && !showDynamicDropdowns[fieldName]) {
      setShowDynamicDropdowns(prev => ({
        ...prev,
        [fieldName]: true
      }));
    }
  };

  // 검색 텍스트 초기화
  const clearSearchText = (fieldName: string) => {
    setSearchTexts(prev => ({
      ...prev,
      [fieldName]: ''
    }));
    setDynamicFields(prev => ({
      ...prev,
      [fieldName]: undefined
    }));
    setShowDynamicDropdowns(prev => ({
      ...prev,
      [fieldName]: false
    }));
  };

  // 강의 목록 필터링
  const getFilteredLectures = (searchText: string) => {
    if (!searchText.trim()) return lectureList;
    
    return lectureList.filter(lecture => 
      lecture.lecture_info.title.toLowerCase().includes(searchText.toLowerCase()) ||
      lecture.lecturer_info.name.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // 커피챗 멘토 목록 불러오기
  const loadCoffeeChatMentors = async () => {
    try {
      setIsLoadingCoffeeChatMentors(true);
      const response = await getCoffeeChatMentors({ isTemp: false }); // 승인된 멘토만 불러오기
      
      if (response.code === 200 || response.code === 1073741824) {
        setCoffeeChatMentors(response.data.content);
      } else {
        /// console.warn('커피챗 멘토 목록 조회 실패:', response.message);
        setCoffeeChatMentors([]);
      }
    } catch (error) {
      /// console.error('커피챗 멘토 목록 불러오기 실패:', error);
      setCoffeeChatMentors([]);
    } finally {
      setIsLoadingCoffeeChatMentors(false);
    }
  };

  // 챕터 URI에서 챕터 번호 추출하는 함수
  const extractChapterNumberFromUri = (uri: string): number => {
    // "/videos/1073670284/chapters/18185489" 형태에서 마지막 숫자 추출
    const match = uri.match(/chapters\/(\d+)$/);
    return match ? parseInt(match[1]) : 0;
  };

  // 강의 선택 시 챕터 목록 불러오기
  const loadLectureChapters = async (lectureId: number) => {
    try {
      setIsLoadingChapters(true);
      const response = await getLectureChapters(lectureId);
      
      if ((response.code === 200 || response.code === 1073741824) && response.data) {
        setChapterList(response.data);
      } else {
        /// console.warn('챕터 목록 조회 실패:', response.message);
        setChapterList([]);
      }
    } catch (error) {
      /// console.error('챕터 목록 불러오기 실패:', error);
      setChapterList([]);
    } finally {
      setIsLoadingChapters(false);
    }
  };

  // 강의 선택 핸들러
  const handleLectureSelect = (fieldName: DynamicFieldName, lectureId: number, lectureTitle: string) => {
    updateDynamicField(fieldName, lectureId);
    setSearchTexts(prev => ({
      ...prev,
      [fieldName]: lectureTitle
    }));
    setShowDynamicDropdowns(prev => ({
      ...prev,
      [fieldName]: false
    }));
    
    // 강의가 선택되면 챕터 목록 불러오기 (강의 질문인 경우에만)
    if (selectedBoardData?.apiCategory === '강의 질문') {
      loadLectureChapters(lectureId);
      // 챕터 필드 초기화
      setDynamicFields(prev => ({
        ...prev,
        chapter: undefined
      }));
    }
  };

  // 선택된 강의 이름 가져오기
  const getSelectedLectureName = (lectureId: number | undefined) => {
    if (!lectureId) return null;
    const lecture = lectureList.find(l => l.lecture_info.id === lectureId);
    return lecture?.lecture_info.title || `강의 ID: ${lectureId}`;
  };

  const handleImageUpload = async (files: FileList): Promise<string[]> => {
    // 이미지 업로드 로직 구현 필요
    const urls: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        urls.push(url);
      }
    }
    
    return urls;
  };

  const handleSubmit = async () => {
    // /// console.log('제출할 content:', content);
    
    // 필수 필드 검증
    if (!validateRequiredFields()) {
      return;
    }

    // 중복 클릭 방지
    if (isApiSubmitting || isSubmitting) {
      return;
    }

    setIsApiSubmitting(true);

    try {
      // 1) Blob URL → 서버 URL로 교체 (첨부/이미지 모두 포함)
      let finalHtml = content;
      if (pendingFiles.length > 0) {
        const dt = new DataTransfer();
        pendingFiles.forEach((f) => dt.items.add(f));
        const { data: uploaded } = await uploadImages(dt.files);
        const serverUrls: string[] = uploaded || [];
        const blobUrls = Array.from(new Set((finalHtml.match(/blob:[^"')\s>]+/g) || [])));
        blobUrls.forEach((blobUrl, idx) => {
          const targetUrl = serverUrls[idx];
          if (targetUrl) {
            const esc = blobUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const re = new RegExp(esc, 'g');
            finalHtml = finalHtml.replace(re, targetUrl);
          }
        });
      }
      if (!selectedBoardData) {
        throw new Error('유효하지 않은 게시판입니다.');
      }

      // HTML 전처리 - div와 br 정리
      const preprocessedHtml = finalHtml.trim()
        .replace(/<div><br><\/div>/g, '<br>')     // 빈 div를 br로
        .replace(/<div>/g, '<br>')                // div 시작을 br로
        .replace(/<\/div>/g, '')                  // div 끝 제거
        .replace(/^<br>/, '');                    // 맨 앞의 불필요한 br 제거

      // API 요청 데이터 구성 (API 스펙에 맞춤)
      const postData: CreatePostRequest & DynamicFormData = {
        title: title.trim(),
        htmlContent: preprocessedHtml,
        category: selectedBoardData.apiCategory,
        pinned: false,
        exposed: true,
        ...dynamicFields, // lectureId, studyCategoryId 등 동적 필드 병합
      };

      /// console.log('API 요청 데이터:', postData);

      // API 호출
      const response = await createPost(postData);
      
      /// console.log('API 응답:', response);

      // HTTP 201 Created 또는 200 OK 응답 확인
      if ((response.code === 201 || response.code === 200) && response.data) {
        // 성공 시 기존 onSubmit 호출 (페이지 이동 등을 위해)
        await onSubmit({
          category: selectedCategory,
          board: selectedBoard,
          title: title.trim(),
          content: content.trim(),
          dynamicFields,
          attachedFiles,
        });
      } else {
        throw new Error(response.message || '게시글 작성에 실패했습니다.');
      }
    } catch (error) {
      /// console.error('게시글 작성 실패:', error);
      
      let errorMessage = '게시글 작성에 실패했습니다.';
      
      if (error instanceof Error) {
        // 네트워크 오류나 서버 오류에 따른 구체적인 메시지
        if (error.message.includes('Failed to fetch')) {
          errorMessage = '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
        } else if (error.message.includes('401')) {
          errorMessage = '로그인이 필요합니다. 다시 로그인해주세요.';
        } else if (error.message.includes('403')) {
          errorMessage = '게시글 작성 권한이 없습니다.';
        } else if (error.message.includes('500')) {
          errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        } else {
          errorMessage = error.message;
        }
      }
      
      alert(errorMessage);
    } finally {
      setIsApiSubmitting(false);
    }
  };

  const getSelectedBoardName = () => {
    const board = boards.find((b) => b.id === selectedBoard);
    return board?.name || '게시판을 선택해주세요.';
  };

  const renderDynamicField = (fieldName: DynamicFieldName, fieldConfig: any) => {
    const value = dynamicFields[fieldName];
    const hasError = !!fieldErrors[fieldName];
    const isRequired = !!currentFieldConfig?.required.includes(fieldName as any);
    const showDropdown = showDynamicDropdowns[fieldName] || false;

    switch (fieldConfig.type) {
      case 'select':
        // lectureId나 lecturerId 필드인 경우 각각 다른 처리
        if (fieldName === 'lectureId') {
          // 기존 강의 목록 사용 (강의 질문용)
          return (
            <DynamicFieldContainer key={fieldName}>
              <DynamicFieldLabel isRequired={isRequired}>
                {fieldConfig.label}
              </DynamicFieldLabel>
              <SelectContainer data-dropdown>
                <SearchInputContainer hasError={hasError}>
                  <SearchInput
                    type="text"
                    placeholder={fieldConfig.placeholder}
                    value={searchTexts[fieldName] || ''}
                    onChange={(e) => updateSearchText(fieldName, e.target.value)}
                    onFocus={() => setShowDynamicDropdowns(prev => ({ ...prev, [fieldName]: true }))}
                    hasError={hasError}
                  />
                  {searchTexts[fieldName] && (
                    <ClearButton
                      type="button"
                      onClick={() => clearSearchText(fieldName)}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </ClearButton>
                  )}
                </SearchInputContainer>
                {showDropdown && (
                  <DropdownMenu>
                    {(() => {
                      const filteredLectures = getFilteredLectures(searchTexts[fieldName] || '');
                      return filteredLectures.length > 0 ? (
                        filteredLectures.map((lecture) => (
                          <DropdownItem
                            key={lecture.lecture_info.id}
                            onClick={() => {
                              handleLectureSelect(fieldName, lecture.lecture_info.id, lecture.lecture_info.title);
                            }}
                          >
                            <LectureOption>
                              <LectureTitle>{lecture.lecture_info.title}</LectureTitle>
                              <LectureInstructor>{lecture.lecturer_info.name}</LectureInstructor>
                            </LectureOption>
                          </DropdownItem>
                        ))
                      ) : lectureList.length === 0 ? (
                        <DropdownItem disabled>
                          <NoLectureMessage>수강 중인 강의가 없습니다</NoLectureMessage>
                        </DropdownItem>
                      ) : (
                        <DropdownItem disabled>
                          <NoLectureMessage>검색 결과가 없습니다</NoLectureMessage>
                        </DropdownItem>
                      );
                    })()}
                  </DropdownMenu>
                )}
              </SelectContainer>
              {hasError && <ErrorMessage>{fieldErrors[fieldName]}</ErrorMessage>}
            </DynamicFieldContainer>
          );
        } else if (fieldName === 'lecturerId') {
        // 연자 선택 (lecturerId) - 간단한 드롭다운
        if (fieldName === 'lecturerId') {
          return (
            <DynamicFieldContainer key={fieldName}>
              <DynamicFieldLabel isRequired={isRequired}>
                {fieldConfig.label}
              </DynamicFieldLabel>
              <SelectContainer data-dropdown>
                <SelectButton
                  onClick={() => toggleDynamicDropdown(fieldName)}
                  hasValue={!!value}
                  hasError={hasError}
                >
                  <SelectValue>
                    {value 
                      ? coffeeChatMentors.find((mentor: CoffeeChatMentor) => mentor.id === value)?.name || `선택됨 (ID: ${value})` 
                      : fieldConfig.placeholder
                    }
                  </SelectValue>
                  <SelectIcon>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </SelectIcon>
                </SelectButton>
                {showDropdown && (
                  <DropdownMenu>
                    {isLoadingCoffeeChatMentors ? (
                      <DropdownItem disabled>
                        <NoLectureMessage>멘토 목록을 불러오는 중...</NoLectureMessage>
                      </DropdownItem>
                    ) : coffeeChatMentors.length > 0 ? (
                      coffeeChatMentors.map((mentor: CoffeeChatMentor) => (
                        <DropdownItem
                          key={mentor.id}
                          onClick={() => {
                            updateDynamicField(fieldName, mentor.id);
                            setShowDynamicDropdowns(prev => ({ ...prev, [fieldName]: false }));
                          }}
                        >
                          <LectureOption>
                            <LectureTitle>{mentor.name}</LectureTitle>
                            <LectureInstructor>{mentor.department}</LectureInstructor>
                          </LectureOption>
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem disabled>
                        <NoLectureMessage>사용 가능한 멘토가 없습니다</NoLectureMessage>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                )}
              </SelectContainer>
              {hasError && <ErrorMessage>{fieldErrors[fieldName]}</ErrorMessage>}
            </DynamicFieldContainer>
          );
        }
        }
        
        // 스터디 카테고리 선택 (studyCategoryId)
        if (fieldName === 'studyCategoryId') {
          return (
            <DynamicFieldContainer key={fieldName}>
              <DynamicFieldLabel isRequired={isRequired}>
                {fieldConfig.label}
              </DynamicFieldLabel>
              <SelectContainer data-dropdown>
                <SelectButton
                  onClick={() => toggleDynamicDropdown(fieldName)}
                  hasValue={!!value}
                  hasError={hasError}
                >
                  <SelectValue>
                    {value ? studyCategories.find((cat: StudyCategory) => cat.id === value)?.name || `선택됨 (ID: ${value})` : fieldConfig.placeholder}
                  </SelectValue>
                  <SelectIcon>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </SelectIcon>
                </SelectButton>
                {showDropdown && (
                  <DropdownMenu>
                    {studyCategories.length > 0 ? (
                      studyCategories.map((category: StudyCategory) => (
                        <DropdownItem
                          key={category.id}
                          onClick={() => {
                            updateDynamicField(fieldName, category.id);
                            setShowDynamicDropdowns(prev => ({ ...prev, [fieldName]: false }));
                          }}
                        >
                          {category.name}
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem disabled>
                        <NoLectureMessage>스터디 카테고리를 불러오는 중...</NoLectureMessage>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                )}
              </SelectContainer>
              {hasError && <ErrorMessage>{fieldErrors[fieldName]}</ErrorMessage>}
            </DynamicFieldContainer>
          );
        }
        
        // 챕터 선택 (chapter)
        if (fieldName === 'chapter') {
          return (
            <DynamicFieldContainer key={fieldName}>
              <DynamicFieldLabel isRequired={isRequired}>
                {fieldConfig.label}
              </DynamicFieldLabel>
              <SelectContainer data-dropdown>
                <SelectButton
                  onClick={() => toggleDynamicDropdown(fieldName)}
                  hasValue={!!value}
                  hasError={hasError}
                >
                  <SelectValue>
                    {value ? chapterList.find((chapter: LectureChapter, index: number) => extractChapterNumberFromUri(chapter.uri) === value)?.title || `챕터 ${value}` : fieldConfig.placeholder}
                  </SelectValue>
                  <SelectIcon>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </SelectIcon>
                </SelectButton>
                {showDropdown && (
                  <DropdownMenu>
                    {isLoadingChapters ? (
                      <DropdownItem disabled>
                        <NoLectureMessage>챕터 목록을 불러오는 중...</NoLectureMessage>
                      </DropdownItem>
                    ) : chapterList.length > 0 ? (
                      chapterList.map((chapter: LectureChapter, index: number) => (
                        <DropdownItem
                          key={chapter.uri}
                          onClick={() => {
                            const chapterNumber = extractChapterNumberFromUri(chapter.uri);
                            updateDynamicField(fieldName, chapterNumber);
                            setShowDynamicDropdowns(prev => ({ ...prev, [fieldName]: false }));
                          }}
                        >
                          <LectureOption>
                            <LectureTitle>{chapter.title}</LectureTitle>
                            <LectureInstructor>챕터 {index + 1}</LectureInstructor>
                          </LectureOption>
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem disabled>
                        <NoLectureMessage>강의를 먼저 선택해주세요</NoLectureMessage>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                )}
              </SelectContainer>
              {hasError && <ErrorMessage>{fieldErrors[fieldName]}</ErrorMessage>}
            </DynamicFieldContainer>
          );
        }
        
        // 다른 select 필드들은 기본 구현
        return (
          <DynamicFieldContainer key={fieldName}>
            <DynamicFieldLabel isRequired={isRequired}>
              {fieldConfig.label}
            </DynamicFieldLabel>
            <SelectContainer data-dropdown>
              <SelectButton
                onClick={() => toggleDynamicDropdown(fieldName)}
                hasValue={!!value}
                hasError={hasError}
              >
                <SelectValue>
                  {value ? `선택됨 (ID: ${value})` : fieldConfig.placeholder}
                </SelectValue>
                <SelectIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </SelectIcon>
              </SelectButton>
            </SelectContainer>
            {hasError && <ErrorMessage>{fieldErrors[fieldName]}</ErrorMessage>}
          </DynamicFieldContainer>
        );

      case 'number':
        return (
          <DynamicFieldContainer key={fieldName}>
            <DynamicFieldLabel isRequired={isRequired}>
              {fieldConfig.label}
            </DynamicFieldLabel>
            <DynamicInput
              type="number"
              placeholder={fieldConfig.placeholder}
              value={value || ''}
              onChange={(e) => updateDynamicField(fieldName, e.target.value)}
              min={fieldConfig.min}
              hasError={hasError}
            />
            {hasError && <ErrorMessage>{fieldErrors[fieldName]}</ErrorMessage>}
          </DynamicFieldContainer>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>글쓰기</Title>
        {/* <CategoryTabs>
          <CategoryTab active={true}>
            질문하기
          </CategoryTab>
          <CategoryTab active={false}>
            글쓰기
          </CategoryTab>
        </CategoryTabs> */}
      </Header>

      {/* Content */}
      <Content>
        {/* Board Selection */}
        <FormGroup>
          <SelectContainer data-dropdown>
            <SelectButton
              onClick={() => setShowBoardDropdown(!showBoardDropdown)}
              hasValue={!!selectedBoard}
            >
              <SelectValue hasValue={!!selectedBoard}>{getSelectedBoardName()}</SelectValue>
              <SelectIcon>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SelectIcon>
            </SelectButton>
            {showBoardDropdown && (
              <DropdownMenu>
                {filteredBoards.map((board) => (
                  <DropdownItem
                    key={board.id}
                    onClick={() => handleBoardSelect(board.id)}
                  >
                    {board.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            )}
          </SelectContainer>
        </FormGroup>

        {/* Dynamic Fields - 커피챗용 */}
        {currentFieldConfig && selectedBoardData?.apiCategory === '커피챗' && (
          <DynamicFieldsSection isNone={false}>
            <DynamicFieldsGrid>
              {renderDynamicField('lecturerId', (currentFieldConfig.fields as any).lecturerId)}
            </DynamicFieldsGrid>
          </DynamicFieldsSection>
        )}

        {/* Dynamic Fields - 기타 필드들 (강의 질문 등) */}
        {currentFieldConfig && selectedBoardData?.apiCategory !== '커피챗' && (
          <>
            {/* 실제로 렌더링할 필드가 있는지 확인 */}
            {(() => {
              const requiredFields = (currentFieldConfig.required as readonly string[])
                .filter((field): field is DynamicFieldName => !['title', 'htmlContent', 'category'].includes(field));
              const optionalFields = currentFieldConfig.optional as readonly string[];
              const hasFields = requiredFields.length > 0 || optionalFields.length > 0;
              
              if (!hasFields) return null; // 필드가 없으면 아무것도 렌더링하지 않음
              
              return (
                <DynamicFieldsSection isNone={false}>
                  {/* <SectionTitle>추가 정보</SectionTitle> */}
                  <DynamicFieldsGrid>
                    {/* 필수 필드 먼저 렌더링 */}
                    {requiredFields.map((fieldName) =>
                      renderDynamicField(
                        fieldName,
                        (currentFieldConfig.fields as Record<string, any>)[fieldName]
                      )
                    )}
                    
                    {/* 선택 필드 렌더링 */}
                    {optionalFields.map((fieldName) =>
                      renderDynamicField(
                        fieldName as DynamicFieldName,
                        (currentFieldConfig.fields as Record<string, any>)[fieldName]
                      )
                    )}
                  </DynamicFieldsGrid>
                </DynamicFieldsSection>
              );
            })()}
          </>
        )}

        {/* Title Input */}
        <FormGroup>
          <TitleInput
            type="text"
            placeholder="제목을 입력해주세요."
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (fieldErrors.title) {
                setFieldErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.title;
                  return newErrors;
                });
              }
            }}
            hasError={!!fieldErrors.title}
          />
          {fieldErrors.title && <ErrorMessage>{fieldErrors.title}</ErrorMessage>}
        </FormGroup>

        {/* Content Editor */}
        <FormGroup>
          <RichTextEditor
            value={content}
            onChange={(newContent) => {
              setContent(newContent);
              if (fieldErrors.content) {
                setFieldErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.content;
                  return newErrors;
                });
              }
            }}
            placeholder="내용을 입력해주세요."
            minHeight="300px"
            onImageUpload={handleImageUpload}
            attachedFiles={attachedFiles}
            onAttachedFilesChange={setAttachedFiles}
            attachmentsEditable={true}
            onPendingFilesChange={setPendingFiles}
          />
          {fieldErrors.content && <ErrorMessage>{fieldErrors.content}</ErrorMessage>}
        </FormGroup>
      </Content>

      {/* Footer */}
      <Footer>
        <CancelButton onClick={onCancel}>취소</CancelButton>
        <SubmitButton
          onClick={handleSubmit}
          disabled={isApiSubmitting || isSubmitting}
        >
          {isApiSubmitting ? '등록 중...' : isSubmitting ? '처리 중...' : '등록하기'}
        </SubmitButton>
      </Footer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 64px 0 128px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 64px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
  }
`;

const Title = styled.h1`
  color: #171719;
  font-size: 36px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 1.33;
  letter-spacing: -0.027em;
  margin: 0;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 10px;
`;

const CategoryTab = styled.button<{ active: boolean }>`
  padding: 9px 12px;
  height: 40px;
  border: ${(props) => props.active ? 'none' : '1px solid rgba(112, 115, 124, 0.16)'};
  background: ${(props) => props.active ? '#448181' : 'transparent'};
  border-radius: 10px;
  color: ${(props) => props.active ? '#F7F7F8' : 'rgba(55, 56, 60, 0.61)'};
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22.01px;
  letter-spacing: 0.14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    background: ${(props) => props.active ? '#3a6f6f' : 'rgba(112, 115, 124, 0.05)'};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-end;
`;

const FormGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
`;

const SelectContainer = styled.div`
  position: relative;
`;

const SelectButton = styled.button<{ hasValue: boolean; hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.22)'};
  border-radius: 12px;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.4)'};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#448181'};
  }
`;

const SelectValue = styled.span<{ hasValue?: boolean }>`
  color: ${props => props.hasValue ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  text-align: left;
`;

const SelectIcon = styled.div`
  color: rgba(55, 56, 60, 0.61);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  box-shadow: 0px 8px 32px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  margin-top: 4px;
  overflow: hidden;
  max-height: 400px;
  overflow-y: auto;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(112, 115, 124, 0.08);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(112, 115, 124, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(112, 115, 124, 0.5);
    }
  }

  /* Firefox 스크롤바 */
  scrollbar-width: thin;
  scrollbar-color: rgba(112, 115, 124, 0.3) rgba(112, 115, 124, 0.08);
`;

const DropdownItem = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: white;
  color: ${props => props.disabled ? 'rgba(171, 171, 171, 0.8)' : '#171719'};
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  text-align: left;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.disabled ? 'white' : 'rgba(112, 115, 124, 0.08)'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(112, 115, 124, 0.08);
  }
`;

// 강의 선택을 위한 새로운 컴포넌트들
const SearchInputContainer = styled.div<{ hasError?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  padding-right: ${props => props.value ? '40px' : '16px'};
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.22)'};
  border-radius: 12px;
  background: white;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:hover {
    border-color: ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.4)'};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#448181'};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(112, 115, 124, 0.2);
  border-radius: 50%;
  color: rgba(55, 56, 60, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(112, 115, 124, 0.3);
    color: rgba(55, 56, 60, 0.8);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const LectureOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LectureTitle = styled.div`
  font-weight: 500;
  color: #171719;
  line-height: 1.4;
`;

const LectureInstructor = styled.div`
  font-size: 12px;
  color: rgba(55, 56, 60, 0.61);
  font-weight: 400;
`;

const NoLectureMessage = styled.div`
  text-align: center;
  color: rgba(55, 56, 60, 0.61);
`;

const TitleInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.22)'};
  border-radius: 12px;
  background: white;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:hover {
    border-color: ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.4)'};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#448181'};
  }
`;

// 동적 필드 관련 스타일
const DynamicFieldsSection = styled.div<{ isNone: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: ${props => props.isNone ? '0' : '16px'};
`;

const SectionTitle = styled.h3`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
`;

const DynamicFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DynamicFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DynamicFieldLabel = styled.label<{ isRequired?: boolean }>`
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DynamicInput = styled.input<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.22)'};
  border-radius: 12px;
  background: white;
  color: #171719;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:hover {
    border-color: ${props => props.hasError ? '#ff6b6b' : 'rgba(112, 115, 124, 0.4)'};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#448181'};
  }
`;

const ErrorMessage = styled.span`
  color: #ff6b6b;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1.4;
  margin-top: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 12px 24px;
  border: none;
  background: transparent;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    color: rgba(55, 56, 60, 0.88);
  }
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  width: 160px;
  height: 48px;
  padding: 0;
  border: none;
  background: ${(props) => props.disabled ? 'rgba(112, 115, 124, 0.22)' : '#448181'};
  color: ${(props) => props.disabled ? 'rgba(55, 56, 60, 0.28)' : 'white'};
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  cursor: ${(props) => props.disabled ? 'not-allowed' : 'pointer'};
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${(props) => props.disabled ? 'rgba(112, 115, 124, 0.22)' : '#3a6f6f'};
  }
`;

export default WritePostForm;